import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, BatteryCharging, KeyRound, Lock, MoveRight, Pencil, Plus, RotateCcw, Undo2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { AssetStatusBadge } from '../components/badges';
import { AddCartDevicesDialog, CartFormModal, LeaseCartDialog, MoveCartDialog, ReturnCartDialog } from '../components/cart-dialogs';
import { useToast } from '../components/toast';
import { Alert, Badge, Button, Card, CardHeader, ConfirmDialog, DescriptionList, EmptyState, ErrorState, PageHeader, PageLoader, Table, Td, Th } from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatDateTime } from '../lib/format';
import { invalidateInventory } from '../lib/hooks';
import type { CartDetail } from '../lib/types';

interface HistoryEvent {
  key: string;
  at: string;
  icon: ReactNode;
  title: string;
  detail: string | null;
  by: string;
}

export function CartDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<'edit' | 'move' | 'lease' | 'return' | 'add' | 'retire' | null>(null);
  const query = useQuery({ queryKey: ['cart', id], queryFn: () => api.get<CartDetail>(`/carts/${id}`) });

  const removeDevice = useMutation({
    mutationFn: (assetId: string) => api.post(`/carts/${id}/assets/remove`, { assetIds: [assetId] }),
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success('Taken off the cart');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const setStatus = useMutation({
    mutationFn: (status: 'ACTIVE' | 'RETIRED') => api.patch(`/carts/${id}`, { status }),
    onSuccess: (_, status) => {
      invalidateInventory(queryClient);
      toast.success(status === 'RETIRED' ? 'Cart retired' : 'Cart back in use');
      setDialog(null);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (query.isLoading) return <PageLoader />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const cart = query.data;
  const canWrite = can('TECHNICIAN');
  const active = cart.status === 'ACTIVE';
  const lease = cart.activeLease;

  const events: HistoryEvent[] = [
    ...cart.moves.map((move) => ({
      key: `move-${move.id}`,
      at: move.movedAt,
      icon: <MoveRight className="size-4" />,
      title: move.fromLabel ? `Moved from ${move.fromLabel} to ${move.toLabel}` : `Moved to ${move.toLabel}`,
      detail: [`${move.deviceCount} device${move.deviceCount === 1 ? '' : 's'} moved with it`, move.note].filter(Boolean).join(' · '),
      by: move.movedByName,
    })),
    ...cart.leases.flatMap((item) => [
      {
        key: `lease-${item.id}`,
        at: item.leasedAt,
        icon: <KeyRound className="size-4" />,
        title: `Leased to ${item.assigneeLabel}`,
        detail: [item.dueAt && `Due ${formatDate(item.dueAt)}`, item.notesOut].filter(Boolean).join(' · ') || null,
        by: item.leasedByName,
      },
      ...(item.returnedAt
        ? [
            {
              key: `return-${item.id}`,
              at: item.returnedAt,
              icon: <RotateCcw className="size-4" />,
              title: `Returned from ${item.assigneeLabel}`,
              detail: item.notesIn,
              by: item.returnedByName ?? 'Someone',
            },
          ]
        : []),
    ]),
  ].sort((a, b) => b.at.localeCompare(a.at));

  return (
    <>
      <PageHeader
        back={{ to: '/carts', label: 'Carts' }}
        title={cart.name}
        meta={
          <>
            {cart.code && <Badge>{cart.code}</Badge>}
            {!active && <Badge>Retired</Badge>}
            {lease && (
              <Badge tone="amber">
                <Lock className="size-3" /> On lease
              </Badge>
            )}
            {cart.homeBuilding.kind === 'WAREHOUSE' && <Badge tone="violet">Off campus</Badge>}
          </>
        }
        actions={
          canWrite && (
            <>
              <Button icon={<Pencil />} onClick={() => setDialog('edit')}>
                Edit
              </Button>
              {active ? (
                <>
                  {cart.deviceCount === 0 && !lease && (
                    <Button variant="ghost" icon={<Archive />} onClick={() => setDialog('retire')}>
                      Retire
                    </Button>
                  )}
                  <Button icon={<MoveRight />} onClick={() => setDialog('move')}>
                    Move cart
                  </Button>
                  {lease ? (
                    <Button variant="primary" icon={<Undo2 />} onClick={() => setDialog('return')}>
                      Return cart
                    </Button>
                  ) : (
                    <Button variant="primary" icon={<KeyRound />} onClick={() => setDialog('lease')}>
                      Lease cart
                    </Button>
                  )}
                </>
              ) : (
                <Button loading={setStatus.isPending} onClick={() => setStatus.mutate('ACTIVE')}>
                  Put back in use
                </Button>
              )}
            </>
          )
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader title="Home location" description="Every device on the cart is kept here" />
            <div className="space-y-4 p-5">
              {lease && (
                <Alert tone="amber" icon={<Lock />} title={`On lease to ${lease.assigneeLabel}`}>
                  Since {formatDate(lease.leasedAt)}
                  {lease.dueAt && `, due back ${formatDate(lease.dueAt)}`}. The home location is locked until it’s returned.
                </Alert>
              )}
              <DescriptionList
                columns={3}
                items={[
                  { label: 'Building', value: cart.homeBuilding.name },
                  {
                    label: 'Room',
                    value: cart.homeRoom && (
                      <Link to={`/locations/rooms/${cart.homeRoom.id}`} className="text-brand-700 hover:underline">
                        {cart.homeRoom.name}
                      </Link>
                    ),
                  },
                  { label: 'Devices', value: `${cart.deviceCount}${cart.capacity ? ` of ${cart.capacity}` : ''}` },
                ]}
              />
              {cart.notes && <p className="text-sm whitespace-pre-wrap text-slate-600">{cart.notes}</p>}
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Devices on this cart"
              actions={
                canWrite &&
                active && (
                  <Button size="sm" icon={<Plus />} onClick={() => setDialog('add')} disabled={!!cart.capacity && cart.deviceCount >= cart.capacity}>
                    Add devices
                  </Button>
                )
              }
            />
            {cart.assets.length === 0 ? (
              <EmptyState icon={<BatteryCharging />} title="No devices on this cart" description="Add devices and they take the cart’s building and room." />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Asset tag</Th>
                    <Th>Name</Th>
                    <Th>Status</Th>
                    <Th>Checked out to</Th>
                    {canWrite && <Th className="text-right">Actions</Th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {cart.assets.map((asset) => (
                    <tr key={asset.id}>
                      <Td>
                        <Link to={`/assets/${asset.id}`} className="font-mono text-xs font-semibold text-brand-800 hover:underline">
                          {asset.assetTag}
                        </Link>
                      </Td>
                      <Td>
                        {asset.name}
                        <p className="text-xs text-slate-500">{asset.serialNumber}</p>
                      </Td>
                      <Td>
                        <AssetStatusBadge status={asset.status} />
                      </Td>
                      <Td className="text-slate-600">{asset.activeCheckout?.assigneeLabel ?? '—'}</Td>
                      {canWrite && (
                        <Td className="text-right">
                          <Button size="sm" variant="ghost" loading={removeDevice.isPending && removeDevice.variables === asset.id} onClick={() => removeDevice.mutate(asset.id)}>
                            Take off
                          </Button>
                        </Td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </div>

        <Card className="h-fit">
          <CardHeader title="Cart checkout history" description="Who moved it, when, and where, and each time it was leased" />
          {events.length === 0 ? (
            <p className="px-5 py-4 text-sm text-slate-500">Not moved or leased yet.</p>
          ) : (
            <ol className="space-y-4 p-5">
              {events.map((event) => (
                <li key={event.key} className="flex gap-3 text-sm">
                  <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700">{event.icon}</span>
                  <div className="min-w-0">
                    <p className="text-slate-800">{event.title}</p>
                    {event.detail && <p className="text-xs text-slate-600">{event.detail}</p>}
                    <p className="text-xs text-slate-500">
                      {event.by} · {formatDateTime(event.at)}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>

      <CartFormModal open={dialog === 'edit'} onClose={() => setDialog(null)} cart={cart} />
      <MoveCartDialog open={dialog === 'move'} onClose={() => setDialog(null)} cart={cart} />
      <LeaseCartDialog open={dialog === 'lease'} onClose={() => setDialog(null)} cart={cart} />
      <ReturnCartDialog open={dialog === 'return'} onClose={() => setDialog(null)} cart={cart} />
      <AddCartDevicesDialog open={dialog === 'add'} onClose={() => setDialog(null)} cart={cart} onCartIds={cart.assets.map((asset) => asset.id)} />
      <ConfirmDialog
        open={dialog === 'retire'}
        onClose={() => setDialog(null)}
        onConfirm={() => setStatus.mutate('RETIRED')}
        loading={setStatus.isPending}
        title={`Retire ${cart.name}?`}
        description="It stops showing with the carts in use. Its history stays, and it can be put back in use."
        confirmLabel="Retire cart"
        tone="primary"
      />
    </>
  );
}
